/**
 * MSAL (PKCE, redirect flow) against the "MOW Lifecycle" app registration. The SPA only ever asks for the
 * API scope `api://<clientId>/access_as_user`; it never holds a Graph token (ADR 058 section 1).
 * Redirect, not popup: works on phones and inside Teams, where popups are blocked.
 */
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined;
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined;

if (!tenantId || !clientId) {
  throw new Error('VITE_ENTRA_TENANT_ID and VITE_ENTRA_CLIENT_ID must be set at build time');
}

export const API_SCOPE = `api://${clientId}/access_as_user`;

export const msal = new PublicClientApplication({
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: { cacheLocation: 'sessionStorage' },
});

let ready: Promise<AuthenticationResult | null> | undefined;

/** Initialise MSAL and finish any in-flight redirect. Resolves to the redirect result (or null). */
export function init(): Promise<AuthenticationResult | null> {
  ready ??= msal.initialize().then(() => msal.handleRedirectPromise());
  return ready;
}

export function account(): AccountInfo | null {
  const active = msal.getActiveAccount();
  if (active) return active;
  const all = msal.getAllAccounts();
  if (all.length) {
    msal.setActiveAccount(all[0]);
    return all[0];
  }
  return null;
}

export async function signIn(): Promise<void> {
  await msal.loginRedirect({ scopes: [API_SCOPE] });
}

export async function signOut(): Promise<void> {
  await msal.logoutRedirect({ account: account() ?? undefined });
}

/** Access token for the API; silent first, redirect if the session needs interaction. */
export async function apiToken(): Promise<string> {
  const acct = account();
  if (!acct) throw new Error('not signed in');
  try {
    const r = await msal.acquireTokenSilent({ scopes: [API_SCOPE], account: acct });
    return r.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ scopes: [API_SCOPE], account: acct });
      return new Promise(() => undefined); // navigating away
    }
    throw e;
  }
}
